/**
 * Reading "has this user confirmed their email" off a user object.
 *
 * The value arrives in three shapes, and which one you get depends on where the
 * user came from:
 *
 * - `GET /users/me2` returns the API body as-is, with `is_validated`.
 * - The Redux user has been through `parsers.user()`, which renames it to
 *   `isValidated`.
 * - The trimmed user on a registration session
 *   (`{ id, email, account_id, created_at, updated_at, validated_at }`) carries
 *   NEITHER flag — only the `validated_at` timestamp. `fetchUserInfo` puts this
 *   copy into Redux via `setCredentials`, and then prefers Redux on the next
 *   read, so this is the shape a resuming reader actually gets.
 *
 * Checking only the boolean keys therefore reported a confirmed user as
 * unconfirmed on every resume — sending them to the email gate, which bounced
 * them onward again 30 seconds later on its own re-check.
 */

interface UserLike {
  is_validated?: boolean | null
  isValidated?: boolean | null
  /**
   * When the email was confirmed. The authoritative field: a timestamp is only
   * ever written once confirmation happened, so its presence IS validation.
   */
  validated_at?: string | null
}

/**
 * Whether the user is confirmed.
 *
 * Any one shape saying yes is enough — a shape simply lacks the other's keys, so
 * an absent value must not veto a present one. Absent in all three reads as
 * false, which is the safe direction: an unconfirmed reader sees the email gate
 * rather than skipping past it.
 */
export const isUserValidated = (user: UserLike | null | undefined): boolean =>
  Boolean(user?.is_validated) ||
  Boolean(user?.isValidated) ||
  Boolean(user?.validated_at)
